import { isEmpty, mergeWith } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DocumentTitle from 'react-document-title';
import { useQuery } from 'react-query';
import {
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { createGlobalStyle, css } from 'styled-components';

import { AppHeader as DsAppHeader } from '@hivebrite/design-system/AppHeader';
import { Box } from '@hivebrite/design-system/Box';
import { TagsList } from '@hivebrite/design-system/TagsList';
import { useBreakpoint, useTheme } from '@hivebrite/design-system/Theme';
import { toast } from '@hivebrite/design-system/Toast';
import { usePrevious } from '@hivebrite/react-hooks';

import { getEventsHeaderControls } from 'hb-react/backoffice/apps/Events/utils/getEventsHeaderControls';
import FiltersApp from 'hb-react/frontoffice/apps/Filters/startup/FiltersAppClient';
import * as PostTypes from 'hb-react/frontoffice/apps/LiveFeed/constants/PostTypes';
import getContentTypesUserCanCreate from 'hb-react/frontoffice/apps/LiveFeed/utils/getContentTypesUserCanCreate';
import getRailsType from 'hb-react/frontoffice/apps/LiveFeed/utils/getRailsType';
import ReportContent from 'hb-react/frontoffice/components/ReportContent';
import UserEventModal from 'hb-react/frontoffice/components/UserEventModal';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import {
  blockUser,
  getBlockedUserIds,
} from 'hb-react/frontoffice/utils/blockUser';
import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';
import EventsIndexContainer from 'hb-react/shared/apps/Events/components/EventsIndexContainer';
import { ViewTypeToggle } from 'hb-react/shared/apps/Events/containers/Index/components/ViewTypeToggle';
import { VIEW_TYPE } from 'hb-react/shared/apps/Events/containers/Index/constants';
import { useEventViewType } from 'hb-react/shared/apps/Events/containers/Index/hooks/useEventViewType';
import { useEventsFilters } from 'hb-react/shared/apps/Events/containers/Index/pages/EventsAppSharedIndex/useEventsFilters';
import { EventsIndexContextProvider } from 'hb-react/shared/apps/Events/contexts/EventsIndexContext';
import getActionItems from 'hb-react/shared/apps/Events/utils/getActionItems';
import { getEventDedicatedPath } from 'hb-react/shared/apps/Events/utils/getEventDedicatedUrl';
import getPageCustomizableHeaderProps from 'hb-react/shared/apps/PageCustomizables/utils/getPageCustomizableHeaderProps';
import AppHeader, {
  useAppHeaderVariant,
} from 'hb-react/shared/components/DesignSystem/AppHeader';
import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';
import ConfirmationModal from 'hb-react/shared/components/DesignSystem/Modal/ConfirmationModal';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import useImmersiveMenu from 'hb-react/shared/hooks/useImmersiveMenu';
import { useMenuName } from 'hb-react/shared/hooks/useMenuName';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';
import { concatArraysCustomizer } from 'hb-react/shared/utils/mergeWith';

import { actionItemsIcons } from './actionItemsIcons';
import EventsMap from './EventsMap';
import EventsMapCard from './EventsMapCard';
import { getFiltersAppConfig } from './utils/getFiltersAppConfig';

interface EventsAppSharedIndexProps {
  config: {
    routes: {
      filters_status_events_path: ({ format }: { format: string }) => string;
      user_event_path: (userId: number, eventId: number) => string;
      user_edit_user_event_path: (
        userId: number,
        eventId: number,
        params: { context: string; topicId: number },
      ) => string;
    };
    topicId: number;
    appPageConfig: {
      name: string;
      featureName: string;
      icon: string;
      iconType: string;
      menu_name: string;
      routes: {
        index: string;
        new: string;
      };
    };
  };
  topic?: any;
}

interface EventItem {
  id: number;
  type: string;
}

const GlobalStyleStorybook = createGlobalStyle`
  body {
    /* Prevents irritating scrolling back and forth when loading more events
    inside multiline groups */
    overflow-anchor: none;
  }
`;

const TAG_NAMES = {
  past: 'past',
  upcoming: 'upcoming',
  thisWeek: 'thisWeek',
  next30Days: 'next30Days',
};

interface QuickFiltersPast {
  name: 'past';
  label: string;
  query: {
    gte_start_date: null;
    lte_start_date: number;
    order: string;
  };
}

interface QuickFiltersUpcoming {
  name: 'upcoming';
  label: string;
  query: {
    gte_start_date: number;
    order: string;
  };
}

interface QuickFiltersThisWeek {
  name: 'thisWeek';
  label: string;
  query: {
    gte_start_date: null;
    active_during_next_x_days: number;
    order: string;
  };
}

interface QuickFiltersNext30Days {
  name: 'next30Days';
  label: string;
  query: {
    gte_start_date: null;
    active_during_next_x_days: number;
    order: string;
  };
}

type QuickFilter =
  | QuickFiltersPast
  | QuickFiltersUpcoming
  | QuickFiltersThisWeek
  | QuickFiltersNext30Days;

const QUICK_FILTERS: QuickFilter[] = [
  {
    name: 'past',
    label: __('Past'),
    query: {
      gte_start_date: null, // Override default of filtersResults
      lte_start_date: moment().valueOf(),
      order: 'desc',
    },
  },
  {
    name: 'upcoming',
    label: __('Upcoming'),
    query: {
      gte_start_date: moment().valueOf(),
      order: 'asc',
    },
  },
  {
    name: 'thisWeek',
    label: __('This week'),
    query: {
      gte_start_date: null, // Override default of filtersResults
      active_during_next_x_days: 7,
      order: 'asc',
    },
  },
  {
    name: 'next30Days',
    label: __('Next 30 days'),
    query: {
      gte_start_date: null, // Override default of filtersResults
      active_during_next_x_days: 30,
      order: 'asc',
    },
  },
];

const getFixedHeaderHeight = (isImmersiveMenu) =>
  isImmersiveMenu
    ? 0
    : Array.from(document.querySelectorAll('header')).reduce(
        (sum, header) => sum + header.getBoundingClientRect().height,
        0,
      );

const getElementHeight = (el) => el?.getBoundingClientRect()?.height || 0;

const getSelectedTagName = (searchParams) =>
  searchParams.get('tag') ?? TAG_NAMES.upcoming;

const EventsAppSharedIndex = ({ config, topic }: EventsAppSharedIndexProps) => {
  const theme = useTheme();
  const { flagDsNewAppHeader } = useFeatureFlag();

  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const topicContext = useTopicContext();
  const breakpoint = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const isImmersiveMenu = useImmersiveMenu({
    location,
    network: currentNetwork,
  });
  const timeZoneConfiguration = useTimeZoneConfiguration();

  const { appPageConfig } = config;

  const userEventMatch = useMatch(`events/p/:userEventId`);

  const { flagEvEventsFoPerformance } = useFeatureFlag();

  const [searchParams, setSearchParams] = useSearchParams();
  const prevSearchParams = usePrevious(searchParams);
  const selectedTagName = getSelectedTagName(searchParams);

  useEffect(() => {
    const newSearchParams = new URLSearchParams(window.location.search);

    if (prevSearchParams?.toString() !== newSearchParams.toString()) {
      setSearchParams(new URLSearchParams(window.location.search));
    }
  }, [prevSearchParams, setSearchParams]);

  const [filtersState, setFiltersState] = useEventsFilters();

  const [tagsListItems, setTagsListItems] = useState<QuickFilter[]>([]);
  const [currentFilterLocationBounds, setCurrentFilterLocationBounds] =
    useState([]);
  const [geoBoundingBoxFilter, setGeoBoundingBoxFilter] = useState({});

  const [isSearchingOnMapBounds, setIsSearchingOnMapBounds] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [reportedContent, setReportedContent] = useState<EventItem | null>(
    null,
  );
  const [hiddenUsersIds, setHiddenUsersIds] = useState(getBlockedUserIds());
  const [itemToDelete, setItemToDelete] = useState<EventItem | null>(null);
  const [eventBeingDeleted, setEventBeingDeleted] = useState<EventItem | null>(
    null,
  );

  const resetFiltersRef = useRef<() => void>(null);
  const refreshMapMarkersRef = useRef<() => void>(null);
  const appHeaderContainerRef = useRef(null);

  const { view } = useEventViewType();

  let eventContentAvailable = false;

  if (currentUser) {
    const { [PostTypes.EVENT]: eventContent } = getContentTypesUserCanCreate({
      currentNetwork,
      currentUser,
      configType: null,
    });

    eventContentAvailable = eventContent.available;
  }

  const appHeaderProps = useMemo(() => {
    const { controls } = getEventsHeaderControls({
      currentUser,
      eventContentAvailable,
    });

    return getPageCustomizableHeaderProps({
      appPageConfig,
      useOldForm: false,
      controls,
    });
  }, [appPageConfig, currentUser, eventContentAvailable]);

  const { variant } = useAppHeaderVariant(appHeaderProps);

  const tagsQuery = useMemo<Record<string, any> | null>(
    () =>
      QUICK_FILTERS.filter(({ name }) => selectedTagName === name).reduce(
        (query, { query: filterQuery }) =>
          mergeWith(null, query, filterQuery, concatArraysCustomizer),
        null,
      ),
    [selectedTagName],
  );

  // Map is only displayed on xl screens
  const isMapDisplayed = useMemo(() => {
    // @ts-expect-error: mock
    if (window.is_from_china) {
      return false;
    }

    return view === VIEW_TYPE.MAP;
  }, [view]);

  const fixedHeaderHeight = getFixedHeaderHeight(isImmersiveMenu);
  // @ts-expect-error: until topicContext is converted to TS
  const topicMenuHeight = getElementHeight(topicContext.topicMenuRef?.current);
  const appHeaderContainerHeight = getElementHeight(
    appHeaderContainerRef?.current,
  );

  // This tries to give the maximum height to the map while keeping the full Events Index App
  // fully visible on the screen. The 40px refers to the padding bottom of the container in which
  // the App is loaded.
  const fixedHeightWithMap = config.topicId
    ? `calc(100vh - ${fixedHeaderHeight}px - ${topicMenuHeight}px - 40px + ${appHeaderContainerHeight}px)`
    : `calc(100vh - ${fixedHeaderHeight}px - ${topicMenuHeight}px + ${appHeaderContainerHeight}px)`;

  useEffect(() => setSelectedEvent(null), [filtersState, selectedTagName]);

  // When hiding the map, we toggle off the isSearchingOnMapBounds and set empty bounds filter
  useEffect(() => {
    if (isMapDisplayed === false && isSearchingOnMapBounds) {
      setIsSearchingOnMapBounds(false);
      setGeoBoundingBoxFilter({});
    }
    if (!isMapDisplayed) {
      setSelectedEvent(null);
    }
  }, [isMapDisplayed, isSearchingOnMapBounds]);

  const handleLocationFilterChange = useCallback((location) => {
    setCurrentFilterLocationBounds(location?.bounds || null);
  }, []);

  const navigateToEvent = useCallback(
    (event) => {
      const eventUrl = getEventDedicatedPath({
        hasUser: currentUser,
        event,
        topicId: config.topicId,
        isWhitelabelGroup: !!topicContext.whitelabelURL,
      });

      navigate(eventUrl);
    },
    [currentUser, navigate],
  );

  const handleEventCardClick = useCallback(
    (event) => {
      if (isMapDisplayed) {
        setSelectedEvent(event);
      } else {
        navigateToEvent(event);
        window.scroll({ top: 0 });
      }
    },
    [isMapDisplayed],
  );

  const filtersAppConfig = getFiltersAppConfig({
    setFilters: setFiltersState,
    isFixedToLeft: isMapDisplayed,
    handleLocationFilterChange,
    topicId: config.topicId,
    currentUser,
    topicContext,
  });

  const now = useMemo(() => moment().valueOf(), []);

  const fetchQueriesTotals = useCallback(
    (queries) =>
      axios
        .post(
          config.routes.filters_status_events_path({
            format: 'json',
          }),
          {
            queries: queries.map(({ query }) => ({
              query,
            })),
          },
        )
        .then(({ data: { totals } }) => totals.map((t) => t > 0))
        .catch((error) => {
          displayXhrError(error);
        }),
    [],
  );

  const { isLoading: isQueriesTotalsLoading, data: queriesTotalsData } =
    useQuery(QUICK_FILTERS, () => fetchQueriesTotals(QUICK_FILTERS), {
      staleTime: 1000 * 60 * 5,
    });

  useEffect(() => {
    if (!isQueriesTotalsLoading && queriesTotalsData) {
      // Keep only the tags for which we have results
      setTagsListItems(
        QUICK_FILTERS.filter((_, index) => queriesTotalsData[index] > 0),
      );
    }
  }, [isQueriesTotalsLoading, queriesTotalsData]);

  const filtersResults = useMemo(
    () => [
      {
        name: __('Events'),
        noResultsMessage: __('No events found'),
        resultsFoundMessage: (count) =>
          n__('%{count} event found', '%{count} events found', count).i__({
            count,
          }),
        showIfEmpty: true,
        routes: config.routes,
        params: {
          include_network_events: true,
          query: {
            // Filter out past events by default
            // gte_start_date: now,
            // default sort by start_date in events_controller
            order: 'desc',
            ...geoBoundingBoxFilter,
            with_location: isEmpty(geoBoundingBoxFilter) ? null : true,
            ...filtersState.filters.query,
            ...tagsQuery,
          },
        },
      },
    ],
    [
      config.routes,
      filtersState.filters.query,
      geoBoundingBoxFilter,
      tagsQuery,
    ],
  );

  const defaultGroups = useMemo(
    () => [
      {
        name: __('Highlighted Events'),
        noResultsMessage: __('No highlighted events found'),
        resultsFoundMessage: (count) =>
          n__(
            '%{count} highlighted event found',
            '%{count} highlighted events found',
            count,
          ).i__({ count }),
        routes: config.routes,
        params: {
          include_network_events: true,
          query: {
            pinned_on_index_page: true,
            gte_start_date: currentNetwork?.features?.events.config
              .upcoming_events_only_in_highlighted
              ? now
              : undefined,
            // default sort by start_date in events_controller
            order: 'asc',
          },
        },
        singleLine: true,
        prefetchPages: flagEvEventsFoPerformance ? 1 : 5, // At a maxPerPage of 4, this will prefetch 20 items
      },
      {
        name: __('Upcoming Events'),
        noResultsMessage: __('No upcoming events found'),
        resultsFoundMessage: (count) =>
          n__(
            '%{count} upcoming event found',
            '%{count} upcoming events found',
            count,
          ).i__({ count }),
        routes: config.routes,
        params: {
          include_network_events: true,
          query: {
            pinned_on_index_page: false,
            gte_start_date: now,
            // default sort by start_date in events_controller
            order: 'asc',
          },
        },
      },
    ],
    // Technically we should depend on config.routes.events_path as well but it's not memoized and down the line it ends
    // up triggering two calls to the backend to load the initial content for each EventGroup because of that. I can't think
    // of a scenario where configs will change between renders so I've ommitted it for now
    [],
  );

  const selectedEventComponent = useMemo(
    () =>
      selectedEvent && (
        <EventsMapCard
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onFindMore={navigateToEvent}
          timezoneConfiguration={timeZoneConfiguration}
        />
      ),
    [selectedEvent, navigateToEvent, timeZoneConfiguration],
  );

  const handleClearFilters = useCallback(() => {
    if (resetFiltersRef.current) {
      resetFiltersRef.current();
    }
  }, []);

  const handleResetFilters = useCallback(() => {
    setSearchParams((searchParams) => {
      searchParams.delete('tag');

      return searchParams;
    });
  }, [setSearchParams]);

  const handleCloseUserEventModal = useCallback(
    () => navigate('.'),
    [navigate],
  );

  const handleHideUser = useCallback(
    (userId) => {
      const hiddenUserIds = blockUser(userId);

      setHiddenUsersIds(hiddenUserIds);
      handleCloseUserEventModal();
    },
    [handleCloseUserEventModal],
  );

  const handleDeleteEvent = useCallback(() => {
    if (!itemToDelete || !currentUser) {
      return;
    }

    setEventBeingDeleted({ id: itemToDelete.id, type: itemToDelete.type });

    axios
      .delete(config.routes.user_event_path(currentUser.id, itemToDelete.id))
      .then(() => {
        // Clear the selected event if it's the deleted event
        if (
          selectedEvent?.id === itemToDelete.id &&
          selectedEvent?.type === itemToDelete.type
        ) {
          setSelectedEvent(null);
        }

        // Refresh the markers to remove the deleted event if it was there
        if (refreshMapMarkersRef?.current) {
          refreshMapMarkersRef.current();
        }

        eventDeletedHandlers.current.forEach((h) => {
          h(itemToDelete);
        });

        handleCloseUserEventModal();
        toast.success(__('Successfully destroyed'));
      })
      .catch(() => toast.error(__('Sorry, an error occurred')))
      .finally(() => setEventBeingDeleted(null));
  }, [itemToDelete]);

  const handleTagChange = useCallback(
    (tags) => {
      setSearchParams((searchParams) => {
        if (tags.length === 0) {
          searchParams.delete('tag');
        } else {
          searchParams.set('tag', tags[0]);
        }

        return searchParams;
      });
    },
    [setSearchParams],
  );

  const eventDeletedHandlers = useRef<((EventItem) => void)[]>([]);

  const subscribeToEventDeletedHandlers = useCallback((handler) => {
    if (!eventDeletedHandlers.current.includes(handler)) {
      eventDeletedHandlers.current.push(handler);
    }
  }, []);

  const unsubscribeFromEventDeletedHandlers = useCallback((handler) => {
    eventDeletedHandlers.current = eventDeletedHandlers.current.filter(
      (h) => h !== handler,
    );
  }, []);

  const groupsToDisplay =
    filtersState.totalSelectedMoreFilters > 0 ||
    !isEmpty(geoBoundingBoxFilter) ||
    selectedTagName !== TAG_NAMES.upcoming
      ? filtersResults
      : defaultGroups;

  const selectedTagNames = useMemo(() => [selectedTagName], [selectedTagName]);

  const reportedContentRailsType =
    reportedContent && getRailsType(reportedContent.type);

  const menuName = useMenuName('events', __('Events'));

  const currentAppHeaderPadding = variant === 'min' ? 0 : 8;
  const appHeaderWrapperPadding = flagDsNewAppHeader
    ? 0
    : currentAppHeaderPadding;

  return (
    <EventsIndexContextProvider
      selectedEvent={selectedEvent}
      setSelectedEvent={setSelectedEvent}
      isMapDisplayed={isMapDisplayed}
      onEventCardClick={handleEventCardClick}
      eventBeingDeleted={eventBeingDeleted}
      hiddenUsersIds={hiddenUsersIds}
      setHiddenUsersIds={setHiddenUsersIds}
      refreshMapMarkersRef={refreshMapMarkersRef}
      subscribeToEventDeletedHandlers={subscribeToEventDeletedHandlers}
      unsubscribeFromEventDeletedHandlers={unsubscribeFromEventDeletedHandlers}
    >
      {reportedContentRailsType && (
        <ReportContent
          id={reportedContent?.id}
          type={reportedContentRailsType}
          onCloseModal={() => setReportedContent(null)}
        />
      )}
      <DocumentTitle
        title={generatePageTitle({
          networkName: getPageTitleFromNetwork(currentNetwork),
          title: menuName,
          topicName: topic?.name,
        })}
      />
      <GlobalStyleStorybook />
      <Box
        flexDirection="column"
        height={isMapDisplayed ? fixedHeightWithMap : 'auto'}
        width="100%"
        pt={config.topicId ? 9 : 0}
      >
        {config.topicId ? null : (
          <Box
            pb={appHeaderWrapperPadding}
            position="relative"
            zIndex={1}
            ref={appHeaderContainerRef}
          >
            {flagDsNewAppHeader ? (
              <DsAppHeader {...appHeaderProps} />
            ) : (
              <AppHeader hideShadow {...appHeaderProps} />
            )}
          </Box>
        )}
        <FiltersApp
          // @ts-expect-error: until FiltersApp gets converted to TS
          config={filtersAppConfig}
          resetFiltersRef={resetFiltersRef}
          onResetFilters={handleResetFilters}
          fixedControls={<ViewTypeToggle />}
          activeExternalFilters={tagsQuery ? 1 : 0}
        />
        <Box
          flex="1 1 100%"
          alignSelf={isMapDisplayed ? 'stretch' : 'center'}
          width={{ _: '100%', m: 'auto' }}
          css={css`
            overflow: hidden;
          `}
        >
          <Grid
            mx={0}
            my={0}
            width={
              isMapDisplayed ? '100%' : { _: '100%', ...theme.breakpoints }
            }
            rowGap={0}
            colGap={0}
            css={css`
              height: 100%;
            `}
          >
            <Row>
              <Col
                width={
                  isMapDisplayed
                    ? `calc(${theme.breakpoints[breakpoint]} / 2)`
                    : 1
                }
                css={css`
                  overflow-x: hidden;
                  overflow-y: auto;
                  height: ${isMapDisplayed ? '100%' : 'auto'};
                `}
              >
                {tagsListItems.length > 0 && (
                  <Box px={{ _: 4, m: 6 }} pt={6} flexWrap="wrap" mb="-12px">
                    <TagsList
                      tags={tagsListItems}
                      onChange={handleTagChange}
                      selectedTagNames={selectedTagNames}
                    />
                  </Box>
                )}
                <Box flexDirection="column" px={{ _: 4, m: 6 }} pt={6} pb={8}>
                  <EventsIndexContainer
                    groups={groupsToDisplay}
                    onClearFilters={handleClearFilters}
                    timezoneConfiguration={timeZoneConfiguration}
                  />
                </Box>
              </Col>
              {isMapDisplayed && (
                // Width 0 may seem weird but flex will take care of stretching the column to fit
                // the available space
                <Col
                  width={0}
                  p={0}
                  css={css`
                    height: 100%;
                  `}
                >
                  <EventsMap
                    config={config}
                    currentFilterLocationBounds={currentFilterLocationBounds}
                    filters={groupsToDisplay.map((g) => g.params)}
                    geoBoundingBoxFilter={geoBoundingBoxFilter}
                    isSearchingOnMapBounds={isSearchingOnMapBounds}
                    setGeoBoundingBoxFilter={setGeoBoundingBoxFilter}
                    setIsSearchingOnMapBounds={setIsSearchingOnMapBounds}
                    selectedEventComponent={selectedEventComponent}
                  />
                </Col>
              )}

              {userEventMatch && (
                <EventModal
                  eventId={userEventMatch.params.userEventId}
                  setItemToDelete={setItemToDelete}
                  handleReportContent={(event) => setReportedContent(event)}
                  handleHideUser={handleHideUser}
                  handleCloseUserEventModal={handleCloseUserEventModal}
                />
              )}
              {itemToDelete && (
                <ConfirmationModal
                  confirmButtonLabel={s__('Events|Delete event')}
                  isLoading={!!eventBeingDeleted}
                  onCloseModal={() => setItemToDelete(null)}
                  onConfirmation={handleDeleteEvent}
                  title={s__('Events|Delete event')}
                  text={s__(
                    'Events|Are you sure you want to delete this event? This action is irreversible.',
                  )}
                />
              )}
            </Row>
          </Grid>
        </Box>
      </Box>
    </EventsIndexContextProvider>
  );
};

const EventModal = ({
  eventId,
  setItemToDelete,
  handleHideUser,
  handleReportContent,
  handleCloseUserEventModal,
}) => {
  const { currentUser, currentNetwork, currentUrl } = useDefaultAppContexts();
  const navigate = useNavigate();

  const createActionsItems = (event) =>
    getActionItems({
      currentUser,
      handleDeleteItem: () => setItemToDelete(event),
      handleEditItem: currentUser
        ? () =>
            navigate(
              Routes.user_edit_user_event_path(currentUser.id, eventId, {
                context: 'events',
                topicId: 0,
              }),
            )
        : () => {},
      handleHideUser: () => handleHideUser(event?.user?.id),
      handleReportContent: () => handleReportContent(event),
      event,
      networkConfig: currentNetwork?.network_config,
      currentUrl,
      icons: actionItemsIcons,
    });

  return (
    <UserEventModal
      eventId={eventId}
      getActionsItems={createActionsItems}
      userId={currentUser?.id}
      onCloseModal={handleCloseUserEventModal}
    />
  );
};

export default EventsAppSharedIndex;
